import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import Logo from './Logo';
import { AuthContext } from '../App';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/career-tools', label: 'Career Tools' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/success-stories', label: 'Success Stories' },
  { to: '/community', label: 'Community' },
  { to: '/affiliate', label: 'Affiliate' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (to) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

  const dashLink = user
    ? { to: user.role === 'admin' ? '/admin' : '/dashboard', label: user.role === 'admin' ? 'Admin Dashboard' : 'Dashboard' }
    : { to: '/login', label: 'Login' };

  return (
    <header className="border-b bg-white sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <Logo showTagline={true} />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`font-medium text-sm transition-colors ${isActive(l.to) ? 'text-[#CE1126]' : 'text-gray-700 hover:text-[#CE1126]'}`}
              aria-current={isActive(l.to) ? 'page' : undefined}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* CTA + auth (always visible on desktop) */}
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/cv"><Button className="btn-uae-red" size="sm" data-testid="header-check-cv-btn">Check My CV (Free)</Button></Link>
          <Link to={dashLink.to}><Button variant="outline" size="sm" data-testid="header-dashboard-btn">{dashLink.label}</Button></Link>
        </div>

        {/* Mobile: CTA + hamburger */}
        <div className="flex lg:hidden items-center gap-2">
          <Link to="/cv"><Button className="btn-uae-red" size="sm" data-testid="header-check-cv-btn-mobile">Check My CV</Button></Link>
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-menu"
            className="p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {open && (
        <nav id="mobile-menu" aria-label="Mobile" className="lg:hidden border-t bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={`block py-2 px-2 rounded-md font-medium text-sm ${isActive(l.to) ? 'text-[#CE1126] bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}
              aria-current={isActive(l.to) ? 'page' : undefined}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {l.label}
            </Link>
          ))}
          <Link to={dashLink.to} onClick={() => setOpen(false)} className="block pt-2">
            <Button variant="outline" size="sm" className="w-full" data-testid="mobile-dashboard-btn">{dashLink.label}</Button>
          </Link>
        </nav>
      )}
    </header>
  );
}
